import React, { useContext, useEffect, useState } from 'react'
import Layout from '../../components/Layout/Layout';
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import AddAppointmentModal from "../../components/AddAppointmentModal/AddAppointmentModal";
import AppointmentTable from "../../components/AppointmentTable/AppointmentTable";
import moment from "moment";
import ApiService from "../../services/ApiService";
import { IAppointment, IAppointmentEvent, IDoctor } from "../../helpers/Interfaces";
import NewButton from "../../components/NewButton/NewButton";
import Show from "../../components/Show/Show";
import EditAppointmentModal from "../../components/EditAppointmentModal/EditAppointmentModal";
import AppointmentModal from "../../components/AppointmentModal/AppointmentModal";
import BulkUploadAppointments from '../../components/BulkUploadAppointments/BulkUploadAppointments';
import { User } from '../../store/Store';
import { isInRole } from '../../helpers/HelperFunctions';
import Loading from '../../components/Loading/Loading';

import "./Appointments.scss";

export default function Appointments() {

  const apiService = new ApiService();

  const [user] = useContext(User);

  const [loading, setLoading] = useState(false);
  const [appointmentModals, setAppointmentModals] = useState({ add: false, edit: false, view: false, bulk: false });
  const [dateRange, setDateRange] = useState([moment(), moment().add(7, "days")]);
  const [showAlert, setShowAlert] = useState({ isError: false, message: "", open: false });
  const [permission, setPermission] = useState([] as string[]);
  const [appointments, setAppointments] = useState([] as IAppointment[]);
  const [selectedAppointment, setSelectedAppointment] = useState({} as IAppointmentEvent);
  const [doctors, setDoctors] = useState<IDoctor[]>([]);
  const [selectedDoctor, setSelectedDoctor] = useState<IDoctor | null>(null);

  useEffect(() => {
    getPermissions();
    getAssigned();
  }, [])

  useEffect(() => {
    if (isInRole(user?.roles, "Manager")) {
      getAppointments();
    } else if (isInRole(user?.roles, "User") && selectedDoctor !== null) {
      getAppointments();
    }
  }, [user, dateRange, selectedDoctor])

  const handleOpen = (modal: "add" | "edit" | "view" | "bulk") => {
    let newAppointmentModals = appointmentModals;
    newAppointmentModals[modal] = true;
    setAppointmentModals((prevDetails) => ({ ...prevDetails, ...newAppointmentModals }));
  }

  const handleClose = (modal: "add" | "edit" | "view" | "bulk", shouldUpdate: boolean) => {
    let newAppointmentModals = appointmentModals;
    newAppointmentModals[modal] = false;
    setAppointmentModals((prevDetails) => ({ ...prevDetails, ...newAppointmentModals }));

    if (shouldUpdate) {
      getAppointments();
    }
  }

  function getAppointments() {

    setLoading(true);

    const data = {
      appointmentStartDate: moment.utc(dateRange[0]),
      appointmentEndDate: moment.utc(dateRange[1]),
      ...(selectedDoctor?.doctorId && { doctorId: selectedDoctor?.doctorId })
    }

    apiService.getAppointmentByDateRange(data).then((result: any) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      setAppointments(data?.appointments);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  function getPermissions() {

    setLoading(true);

    let data = {
      page: "Appointments",
    };

    apiService.getPermission(data).then((result: any) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        return;
      }

      setPermission(data?.actions);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
    }).finally(() => {
      setLoading(false);
    });
  }

  function getAssigned() {

    setLoading(true);

    apiService.getAssignedDoctors().then((result) => {

      let data = result.data;

      if (!data?.isSuccessful) {
        let newShowAlert = showAlert;
        newShowAlert.isError = true;
        newShowAlert.message = data?.errorMessage;
        newShowAlert.open = true;
        setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
        setLoading(false);
        return;
      }

      const assigned: IDoctor[] = data?.assigned;

      setDoctors(assigned);
      setSelectedDoctor(assigned[0]);
    }).catch(() => {
      let newShowAlert = showAlert;
      newShowAlert.isError = true;
      newShowAlert.message = "Unexpected Error";
      newShowAlert.open = true;
      setShowAlert((prevDetails) => ({ ...prevDetails, ...newShowAlert }));
    }).finally(() => {
      setLoading(false);
    });
  }

  return (
    <Layout selectedIndex={1}>
      <div className="appointment-content">
        <div className="appointment">
          <div className="appointment-header container-header animated animatedFadeInUp fadeInUp">
            <div className="header-data">
              <span className="appointment-account-title">My Appointments</span>
            </div>
            <div className="add-appointment">
              <div className="status">
                <DateRangePicker startPlaceHolder="FROM" endPlaceHolder="TO"
                  onChange={(startDate, endDate) => {
                    let newDateRange = [moment.utc(startDate).local(), moment.utc(endDate).local()];
                    setDateRange(newDateRange);
                  }} />
              </div>
              <Show hasAccess={permission?.includes("Create")}>
                <NewButton text="+ Add Appointment" onClick={() => handleOpen("add")}></NewButton>
              </Show>
              <Show hasAccess={permission?.includes("Create")}>
                <NewButton text="+ Bulk Upload" onClick={() => handleOpen("bulk")}></NewButton>
              </Show>
            </div>
          </div>
          <div className="select-clinic-wrapper main-content-gap animated animatedFadeInUp fadeInUp">
            {doctors.map((doctor) => {
              return (
                <div className={`select-clinic ${selectedDoctor?.doctorId === doctor.doctorId ? 'selected' : ''}`}
                  onClick={() => { setSelectedDoctor(doctor); }}>
                  <div>
                    <span className="left">Doctor: </span>
                  </div>
                  <div>
                    <span>{doctor.doctor}</span>
                  </div>
                </div>
              )
            })}
          </div>

          {appointments.length > 0
            ? (<div className="appointment-content-body">
              <Show hasAccess={permission?.includes("Read")}>
                <div className="appointment-range-table animated animatedFadeInUp fadeInUp">
                  <AppointmentTable dateRange={dateRange} appointments={appointments} openModal={handleOpen}
                    setSelectedAppointment={setSelectedAppointment} />
                </div>
              </Show>
            </div>)
            : (<div style={{ display: "flex", justifyContent: "center" }}>
              <Loading color='orange' />
            </div>)
          }
        </div>

        {appointmentModals.add
          ? (<AddAppointmentModal open={appointmentModals.add} closeModal={handleClose} appointments={appointments}
            selectedDoctor={selectedDoctor} />)
          : (<></>)
        }
        {appointmentModals.edit
          ? (<EditAppointmentModal appointmentId={selectedAppointment.appointmentId} open={appointmentModals.edit}
            closeModal={handleClose} appointments={appointments} selectedDoctor={selectedDoctor} />)
          : (<></>)
        }
        {appointmentModals.view
          ? (<AppointmentModal open={appointmentModals.view} selectedAppointment={selectedAppointment} openModal={handleOpen}
            closeModal={handleClose} />)
          : (<></>)
        }
        {appointmentModals.bulk
          ? (<BulkUploadAppointments open={appointmentModals.bulk} openModal={handleOpen} closeModal={handleClose}
            selectedDoctor={selectedDoctor} />)
          : (<></>)
        }
      </div>
    </Layout >
  )
}